import React, { useState } from 'react';
import {
  BarChart3,
  ArrowLeft,
  Search,
  Filter,
  Plus,
  Eye,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Calendar,
  Package,
  Star,
  Building,
  FileText,
  TrendingUp,
  TrendingDown,
  Award,
  AlertCircle
} from 'lucide-react';
import { supplierQuotations, rfqData, suppliers } from '../../data/supplierData';

const QuotationComparison = ({ onBack, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedRFQ, setSelectedRFQ] = useState(null);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [selectedQuotationsForComparison, setSelectedQuotationsForComparison] = useState([]);

  // Get supplier name by ID
  const getSupplierName = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier ? supplier.name : supplierId;
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'text-blue-600 bg-blue-100';
      case 'under_review': return 'text-yellow-600 bg-yellow-100';
      case 'accepted': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'submitted': return '已提交';
      case 'under_review': return '審核中';
      case 'accepted': return '已接受';
      case 'rejected': return '已拒絕';
      default: return '未知';
    }
  };

  // Handle quotation selection for comparison
  const handleQuotationSelect = (quotationId) => {
    if (comparisonMode) {
      setSelectedQuotationsForComparison(prev => {
        if (prev.includes(quotationId)) {
          return prev.filter(id => id !== quotationId);
        } else if (prev.length < 3) {
          return [...prev, quotationId];
        }
        return prev;
      });
    } else {
      setSelectedQuotation(quotationId);
      setActiveView('detail');
    }
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">報價比較</h1>
          <p className="text-gray-600 mt-1">比較供應商報價，選擇最佳方案</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setActiveView('rfq_list')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增詢價</span>
          </button>
          <button
            onClick={() => setActiveView('quotation_list')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Eye className="w-4 h-4" />
            <span>查看所有報價</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">總詢價單</p>
              <p className="text-2xl font-bold text-gray-900">{rfqData.length}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-sm text-green-600">+3 本月新增</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">收到報價</p>
              <p className="text-2xl font-bold text-gray-900">{supplierQuotations.length}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <BarChart3 className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <span className="text-sm text-gray-600">平均 {(supplierQuotations.length / rfqData.length).toFixed(1)} 份/詢價</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">平均報價金額</p>
              <p className="text-2xl font-bold text-gray-900">
                HK${(supplierQuotations.reduce((sum, q) => sum + q.totalAmount, 0) / supplierQuotations.length / 1000).toFixed(0)}K
              </p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <DollarSign className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingDown className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-sm text-green-600">-5% 比上月</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">平均響應時間</p>
              <p className="text-2xl font-bold text-gray-900">2.3天</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-red-500 mr-1" />
            <span className="text-sm text-red-600">+0.5天 比上月</span>
          </div>
        </div>
      </div>

      {/* Active RFQs */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">進行中的詢價</h2>
          <button
            onClick={() => setActiveView('rfq_list')}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            查看全部
          </button>
        </div>

        <div className="space-y-4">
          {rfqData.filter(rfq => rfq.status === 'active').map((rfq) => (
            <div key={rfq.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{rfq.title}</h3>
                  <p className="text-sm text-gray-600">{rfq.rfqNumber}</p>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-sm text-gray-600">截止日期</p>
                    <p className="font-medium text-gray-900">{rfq.responseDeadline}</p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedRFQ(rfq.id);
                      setActiveView('rfq_detail');
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    查看詳情
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">邀請供應商:</span>
                  <span className="ml-1 font-medium">{rfq.invitedSuppliers.length}</span>
                </div>
                <div>
                  <span className="text-gray-600">收到報價:</span>
                  <span className="ml-1 font-medium">{rfq.receivedQuotations.length}</span>
                </div>
                <div>
                  <span className="text-gray-600">響應率:</span>
                  <span className="ml-1 font-medium">
                    {Math.round((rfq.receivedQuotations.length / rfq.invitedSuppliers.length) * 100)}%
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">剩餘時間:</span>
                  <span className="ml-1 font-medium text-orange-600">3天</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Quotations */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">最新報價</h2>
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setComparisonMode(!comparisonMode);
                setSelectedQuotationsForComparison([]);
              }}
              className={`px-4 py-2 rounded-lg transition-colors ${
                comparisonMode 
                  ? 'bg-orange-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {comparisonMode ? '退出比較模式' : '比較模式'}
            </button>
            {comparisonMode && selectedQuotationsForComparison.length >= 2 && (
              <button
                onClick={() => setActiveView('comparison')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                開始比較 ({selectedQuotationsForComparison.length})
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {comparisonMode && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    選擇
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  報價編號
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  供應商
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  報價日期
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  總金額
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  有效期
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  狀態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {supplierQuotations.map((quotation) => (
                <tr 
                  key={quotation.id} 
                  className={`hover:bg-gray-50 ${
                    selectedQuotationsForComparison.includes(quotation.id) ? 'bg-blue-50' : ''
                  }`}
                >
                  {comparisonMode && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedQuotationsForComparison.includes(quotation.id)}
                        onChange={() => handleQuotationSelect(quotation.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        disabled={!selectedQuotationsForComparison.includes(quotation.id) && selectedQuotationsForComparison.length >= 3}
                      />
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {quotation.quotationNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getSupplierName(quotation.supplierId)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {quotation.quotationDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    HK${quotation.totalAmount.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {quotation.validUntil}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(quotation.status)}`}>
                      {getStatusText(quotation.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleQuotationSelect(quotation.id)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      查看
                    </button>
                    <button className="text-gray-600 hover:text-gray-900">
                      下載
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderQuotationComparison = () => {
    const selectedQuotations = supplierQuotations.filter(q => 
      selectedQuotationsForComparison.includes(q.id)
    );

    if (selectedQuotations.length < 2) {
      return (
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">請選擇至少兩個報價進行比較</h3>
          <button
            onClick={() => setActiveView('dashboard')}
            className="text-blue-600 hover:text-blue-800"
          >
            返回選擇報價
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">報價比較</h1>
            <p className="text-gray-600 mt-1">比較 {selectedQuotations.length} 個供應商報價</p>
          </div>
          <button
            onClick={() => setActiveView('dashboard')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回</span>
          </button>
        </div>

        {/* Comparison Summary */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">比較概覽</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">最低報價</p>
              <p className="text-2xl font-bold text-green-600">
                HK${Math.min(...selectedQuotations.map(q => q.totalAmount)).toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">
                {getSupplierName(selectedQuotations.find(q => q.totalAmount === Math.min(...selectedQuotations.map(q => q.totalAmount))).supplierId)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">最高報價</p>
              <p className="text-2xl font-bold text-red-600">
                HK${Math.max(...selectedQuotations.map(q => q.totalAmount)).toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">
                {getSupplierName(selectedQuotations.find(q => q.totalAmount === Math.max(...selectedQuotations.map(q => q.totalAmount))).supplierId)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">價差</p>
              <p className="text-2xl font-bold text-gray-900">
                HK${(Math.max(...selectedQuotations.map(q => q.totalAmount)) - Math.min(...selectedQuotations.map(q => q.totalAmount))).toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">
                {(((Math.max(...selectedQuotations.map(q => q.totalAmount)) - Math.min(...selectedQuotations.map(q => q.totalAmount))) / Math.min(...selectedQuotations.map(q => q.totalAmount))) * 100).toFixed(1)}% 差異
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">平均報價</p>
              <p className="text-2xl font-bold text-gray-900">
                HK${(selectedQuotations.reduce((sum, q) => sum + q.totalAmount, 0) / selectedQuotations.length).toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">
                {selectedQuotations.length} 個報價
              </p>
            </div>
          </div>
        </div>

        {/* Detailed Comparison */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50">
                    比較項目
                  </th>
                  {selectedQuotations.map((quotation) => (
                    <th key={quotation.id} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {getSupplierName(quotation.supplierId)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">
                    報價編號
                  </td>
                  {selectedQuotations.map((quotation) => (
                    <td key={quotation.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {quotation.quotationNumber}
                    </td>
                  ))}
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-gray-50">
                    總金額
                  </td>
                  {selectedQuotations.map((quotation) => (
                    <td key={quotation.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`font-semibold ${
                        quotation.totalAmount === Math.min(...selectedQuotations.map(q => q.totalAmount)) 
                          ? 'text-green-600' 
                          : quotation.totalAmount === Math.max(...selectedQuotations.map(q => q.totalAmount))
                          ? 'text-red-600'
                          : 'text-gray-900'
                      }`}>
                        HK${quotation.totalAmount.toLocaleString()}
                      </span>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">
                    小計
                  </td>
                  {selectedQuotations.map((quotation) => (
                    <td key={quotation.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      HK${quotation.subtotal.toLocaleString()}
                    </td>
                  ))}
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-gray-50">
                    運費
                  </td>
                  {selectedQuotations.map((quotation) => (
                    <td key={quotation.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      HK${quotation.shippingCost.toLocaleString()}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">
                    折扣
                  </td>
                  {selectedQuotations.map((quotation) => (
                    <td key={quotation.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {quotation.discount > 0 ? `${quotation.discount}%` : '-'}
                    </td>
                  ))}
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-gray-50">
                    付款條件
                  </td>
                  {selectedQuotations.map((quotation) => (
                    <td key={quotation.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {quotation.terms.payment}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">
                    交貨條件
                  </td>
                  {selectedQuotations.map((quotation) => (
                    <td key={quotation.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {quotation.terms.delivery}
                    </td>
                  ))}
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-gray-50">
                    保固期
                  </td>
                  {selectedQuotations.map((quotation) => (
                    <td key={quotation.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {quotation.terms.warranty}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">
                    有效期
                  </td>
                  {selectedQuotations.map((quotation) => (
                    <td key={quotation.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {quotation.validUntil}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Recommendation */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">推薦建議</h2>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Award className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold text-green-800">推薦供應商</h3>
            </div>
            <p className="text-green-700">
              基於價格、條件和供應商評級，推薦選擇 
              <span className="font-semibold">
                {getSupplierName(selectedQuotations.find(q => q.totalAmount === Math.min(...selectedQuotations.map(q => q.totalAmount))).supplierId)}
              </span> 
              的報價。
            </p>
            <div className="mt-3 flex space-x-3">
              <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                接受報價
              </button>
              <button className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors">
                發送議價
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'comparison':
        return renderQuotationComparison();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center space-x-4">
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>返回採購管理</span>
        </button>
      </div>

      {renderContent()}
    </div>
  );
};

export default QuotationComparison; 